import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  input,
  ViewChild,
  TemplateRef,
  Renderer2,
  ChangeDetectorRef,
  Optional,
} from '@angular/core';
import { FloorService } from '../../../core/services/floor.service';
import { OrganizationService } from '../../../core/services/organization.service';
import { CameraService } from '../../../core/services/camera.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { DetailsModalComponent } from '../organization/details-modal/details-modal.component';
import { catchError, map, Observable, of, switchMap, Subject, takeUntil } from 'rxjs';
import _ from 'lodash';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FloorMapComponent } from '../floor-map/floor-map.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StreetViewMapComponent } from '../street-view-map/street-view-map.component';
import { ArcAngelPlaybackModeService } from '../../../core/services/arc-angel-playback-mode.service';
import moment from 'moment';
import { IncidentCategoryService } from '../../../core/services/incident-category.service';
import { ArcAngelService } from '../../../core/services/arc-angel.service';
import { CoresService } from '../../../core/services/cores.service';
import { SessionOptionsService } from '../../../core/services/session-option.service';
import { ArcAngelLiveModeService } from '../../../core/services/arc-angel-live-mode.service';
import { ResponseModalComponent } from '../response-modal/response-modal.component';
import { MultipleMessageComponent } from '../organization/multiple-message/multiple-message.component';
import { CreateUpdateDialogComponent } from '../create-update-dialog/create-update-dialog.component'; // adjust path if needed
import * as Cesium from 'cesium';
import { PluginContextService } from '../../../core/services/plugin-context.service';
import { DataContextService } from '../../../core/services/data-context.service';
import { DeviceService } from '../../../core/services/device.service';
import { GlobeService } from '../../../core/services/globe.service';
import { WindowService } from '../../../core/services/window.service';
import { ArcAngelMonitorModeService } from '../arc-angel-monitor-mode.service';

interface User {
  name: string;
  src: string;
  visible: boolean;
  status: string;
  deviceId?: string;
}
interface Perimeter {
  getName(): string;
  getNumberOfUnknownUsers(): number;
  getNumberOfSafeUsers(): number;
  getNumberOfUnSafeUsers(): number;
  // ...other methods as needed
}

interface SafeZone {
  name: string;
  cesiumEntity: { show: boolean };
}

interface Incident {
  safezones: SafeZone[];
}

interface MapEntity {
  incidents: Incident[];
}

export interface SopTaskStep {
  id: number;
  order: number;
  label: string;
  estimatedTime: string;
  isCompleted: 'Yes' | 'No';
}

export interface SopTask {
  label: string;
  type: string;
  stepsCompleted: number;
  totalNumberOfSteps: number;
}

export interface SopInfo {
  sopTask: SopTask;
  sopTaskStep: SopTaskStep[];
}
@Component({
  selector: 'app-incident-visualizer',
  standalone: true,
  imports: [[CommonModule, FormsModule, StreetViewMapComponent]],
  templateUrl: './incident-visualizer.component.html',
  styleUrl: './incident-visualizer.component.css',
})
export class IncidentVisualizerComponent implements OnDestroy {
  @ViewChild('noUsersModal') noUsersModal!: TemplateRef<any>;

  @Input() floorMapEntity: any = {};
  @Input() mapentityid: any;
  @Input() incident: any;
  @Input() isPlaybackMode: boolean = false;
  @Input() liveData: any
  @Output() closeRequested = new EventEmitter<void>();
  @Input() tempLat: number = 0.0;
  @Input() tempLong: number = 0.0;
  @Input() buildingId: any;

  longitude: any;
  latitude: any;
  incidents: any;
  showCameraLayer = false;
  showAccessControlLayer = false;
  showBeaconLayer = false;
  numberOfCameras = 0;
  numberOfAccessControls = 0;
  numberOfBeacons = 0;
  leafletFloorHeight = '400px';
  expandLeaflet = false;
  tabHeight = '300px';
  data = {
    totalCount: 0,
    dangerCount: 0,
    safeCount: 0,
    unknownCount: 0,
    suspectCount: 0,
    totalUserCount: 0,
    page: 1,
    lastPage: 1,
    limit: 30,
    threshold: 250,
    gridSize: 10,
    userList1: [] as User[],
    userList2: [] as User[],
    userList3: [] as User[],
    // aggregated list of all users (kept on data so template type-checker can see it)
    allUsers: [] as User[],
    dangerChecked: true,
    safeChecked: true,
    unknownChecked: true,
    suspectChecked: true,
    show3DUserClusters: true,
  };
  fullUsers: User[] = [];
  updates: any;
  scrollHeight = '200px';
  sopInfo: SopInfo = {
    sopTask: { label: '', type: '', stepsCompleted: 0, totalNumberOfSteps: 0 },
    sopTaskStep: []
  };
  cameraListForFloor: any[] = [];
  perimeters: any[] = [];
  beaconsVisible = false;
  activeTab = 'user';
  cameraVisible = true;
  showTabs: boolean = true;
  @Input() highGeometryLocation: any = [];
  enableInteraction: boolean = false;
  enableMonitor: boolean = false;
  enablePlayback: boolean = false;
  icon: string = 'assets/img/default_entity.png';
  commands: any[] = [];
  showSafeZones: boolean = true;
  showUpdates: boolean = true;
  currUpdates: any[] = [];
  allUsers: any;
  // Search UI state
  searchVisible: boolean = false;
  searchQuery: string = '';
  filteredUsers: User[] = [];

  // Track if status filter is active to prevent userOnFloorsChange from overwriting
  private isStatusFilterActive: boolean = false;

  // Keep original lists so we can restore them after clearing search
  private origUserList1: User[] = [];
  private origUserList2: User[] = [];
  private origUserList3: User[] = [];
  showGlobeClick = false;

  // Track user visibility state to preserve across data refreshes
  private userVisibilityMap: Map<string, boolean> = new Map();
  @Output() addSafeZoneStatus = new EventEmitter<boolean>();

  private destroy$ = new Subject<void>();
  isSafeZoneActive = false;

  constructor(
    private floorService: FloorService,
    private modalService: NgbModal,
    private organizationService: OrganizationService,
    private spinnerService: NgxSpinnerService,
    private arcAngelPlaybackMode: ArcAngelPlaybackModeService,
    private incidentCategoryService: IncidentCategoryService,
    private arcAngelService: ArcAngelService,
    private core: CoresService,
    private sessionOptionService: SessionOptionsService,
    private arcAngelLiveMode: ArcAngelLiveModeService,
    private pluginContext: PluginContextService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private deviceService: DeviceService,
    private windowService: WindowService,
    private globeService: GlobeService,
    private dataContext: DataContextService,
    @Optional() private arcAngelMonitorModeService?: ArcAngelMonitorModeService
  ) { }
  ngOnInit() {
    this.floorService.safeZoneStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.isSafeZoneActive = status.isActive;
        try { this.cdr.markForCheck(); } catch (e) {}
      });

    this.enableInteraction = this.sessionOptionService.isLiveMode();
    this.enableMonitor = this.sessionOptionService.isMonitorMode();
    this.enablePlayback = this.sessionOptionService.isPlaybackMode();
    if (this.mapentityid && typeof this.mapentityid.iconUrl === 'string') {
      this.icon = this.mapentityid.iconUrl;
    } else if (this.mapentityid && (this.mapentityid as any).icon) {
      this.icon = (this.mapentityid as any).icon;
    }
    const arcLiveEntity = this.liveData || this.mapentityid;
    const allCommands = typeof arcLiveEntity.getCommands === 'function'
      ? arcLiveEntity.getCommands()
      : Array.isArray(arcLiveEntity.commands) ? arcLiveEntity.commands : [];

    const mode = this.sessionOptionService.getMode();
    for (const command of allCommands) {
      if (command.shouldShow ? command.shouldShow(mode) : true) {
        this.commands.push(command);
        if (typeof command.init === 'function') {
          command.init(arcLiveEntity);
        }
      }
    }
  }

  getStartTime(): string {
    if (this.mapentityid && this.mapentityid.obj?.createdAt) {
      return this.mapentityid.obj.createdAt.replace(/\..*$/, '').replace('T', ' ');
    }
    return '';
  }

  getEndTime(): string {
    if (this.mapentityid && this.mapentityid.obj?.endedAt) {
      return this.mapentityid.obj.endedAt.replace(/\..*$/, '').replace('T', ' ');
    } else if (this.mapentityid && this.mapentityid.obj?.updatedAt) {
      return this.mapentityid.obj.updatedAt.replace(/\..*$/, '').replace('T', ' ');
    }
    return '';
  }

  getTotalElapsedTimestamp(): number {
    let start: string | undefined;
    if (this.mapentityid?.obj?.createdAt) {
      start = this.mapentityid.obj.createdAt;
    }

    let end: string | Date | undefined;
    if (this.mapentityid?.obj?.endedAt) {
      end = this.mapentityid.obj.endedAt;
    } else if (this.mapentityid?.obj?.updatedAt) {
      end = this.mapentityid.obj.updatedAt;
    } else {
      end = new Date();
    }

    let t = 0;
    if (start && end) {
      t = new Date(end).getTime() - new Date(start).getTime();
    }

    return t;
  }

  getTotalElapsedTime(): string {
    const t = Math.round(this.getTotalElapsedTimestamp() / 1000);
    return this.secToStr(t);
  }

  getElapsedTimePct(): number {
    const elapsed = this.core.getCurrentTimestamp() - this.incident?.timestamp;
    const total = this.getTotalElapsedTimestamp();
    let pct = Math.floor((elapsed / total) * 100);

    if (isNaN(pct) || pct <= 1) {
      pct = 0;
    }
    if (pct >= 99) {
      pct = 100;
    }
    if (elapsed <= 2000) {
      pct = 0;
    }

    return pct;
  }

  // Helper method to convert seconds to a human-readable duration
  private secToStr(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  isEmpty(value: any): boolean {
    return value === '' || value === null || value === undefined;
  }

  round(value: any): number | string {
    if (value === undefined || value === null || isNaN(value)) return '';
    return Math.round(parseFloat(value) * 1000000) / 1000000;
  }

  showStaticCoordinates(): boolean {
    const incident = this.getCurrentIncident();
    this.latitude =
      incident?.latitude !== undefined
        ? incident.latitude
        : incident?.centerLatitude;
    this.longitude =
      incident?.longitude !== undefined
        ? incident.longitude
        : incident?.centerLongitude;
    return !!(this.latitude && this.longitude);
  }

  showTemporalCoordinates(): boolean {
    return this.mapentityid?.type === 'UnitMapEntity';
  }
  formatDate(value: any): string {
    return value ? moment(value).format('MMMM Do YYYY, h:mm:ss a') : '';
  }
  getCurrentIncident(): any {
    if (
      this.mapentityid &&
      (this.mapentityid.type === 'BuildingMapEntity' ||
        this.mapentityid.constructor?.name === 'BuildingMapEntity') &&
      Array.isArray(this.mapentityid.incidents)
    ) {
      return this.mapentityid.incidents[0];
    }
    return this.mapentityid ?? this.mapentityid?.incidents?.[0];
  }
  showHideCameraLayer() {
    /* ... */
  }
  showHideAccessControlLayer() {
    /* ... */
  }
  showHideBeaconLayer() {
    /* ... */
  }
  sendMessageForFloor() {
    let incidentId: string | undefined;
    if (this.enableInteraction) {
      incidentId = this.liveData.eventId;
    } else if (this.floorMapEntity?.incidents && this.floorMapEntity.incidents.length > 0) {
      incidentId = this.floorMapEntity.incidents[0].eventId;
    }
    if (!incidentId) {
      return;
    }

    const statuses = ['DANGER', 'SAFE', 'UNKNOWN'];
    const perimeterId = this.floorMapEntity?.perimeterId;
    const recipients = `users on ${this.floorMapEntity?.name} with status of ${statuses.join(', ')} (total ${this.data.totalUserCount})`;

    const options = {
      useApi: true,
      statuses,
      perimeterId,
      incidentId,
      recipients
    };

    this.openMultipleMessageModalWithOptions('Send Message', options);
  }
  toggle3DUserClusters() {
    this.data.show3DUserClusters = !this.data.show3DUserClusters;
  }

  statusChanged() {
    const checkedStatuses: string[] = [];

    if (this.data.dangerChecked) checkedStatuses.push('DANGER');
    if (this.data.safeChecked) checkedStatuses.push('SAFE');
    if (this.data.unknownChecked) checkedStatuses.push('UNKNOWN');
    if (this.data.suspectChecked) checkedStatuses.push('SUSPECT');

    // Set flag to prevent userOnFloorsChange from overwriting
    // Filter is active when 0-3 checkboxes are checked (not all 4)
    this.isStatusFilterActive = checkedStatuses.length < 4;

    let filteredUsers: User[] = [];

    // If nothing selected → show NOTHING
    if (checkedStatuses.length > 0) {
      filteredUsers = this.fullUsers.filter(user =>
        checkedStatuses.includes(user.status)
      );
    }

    // Distribute into 3 columns
    const totalUsers = filteredUsers.length;
    const col1End = Math.ceil(totalUsers / 3);
    const col2End = Math.ceil((totalUsers * 2) / 3);

    this.data.userList1 = filteredUsers.slice(0, col1End);
    this.data.userList2 = filteredUsers.slice(col1End, col2End);
    this.data.userList3 = filteredUsers.slice(col2End);

    this.data.totalUserCount = filteredUsers.length;
    this.data.lastPage = Math.max(1, Math.ceil(filteredUsers.length / this.data.limit));
    this.data.page = 1;

    this.cdr.detectChanges();
  }

  sendMessageToUsers(status: string) {
    let incidentId: string | undefined;
    if (this.enableInteraction && this.liveData) {
      incidentId = this.liveData.eventId;
    } else if (this.floorMapEntity?.incidents && this.floorMapEntity.incidents.length > 0) {
      incidentId = this.floorMapEntity.incidents[0].eventId;
    }
    if (!incidentId) {
      return;
    }

    let totalUsers = 0;
    if (status === 'DANGER') totalUsers = this.data.dangerCount;
    if (status === 'SAFE') totalUsers = this.data.safeCount;
    if (status === 'UNKNOWN') totalUsers = this.data.unknownCount;
    if (status === 'SUSPECT') totalUsers = this.data.suspectCount;

    if (totalUsers === 0) {
      const modalRef = this.modalService.open(ResponseModalComponent, {
        centered: true,
        backdrop: 'static'
      });
      modalRef.componentInstance.message = 'No user to contact';
      return;
    }

    const statuses = [status];
    const perimeterId = this.floorMapEntity?.perimeterId;
    const recipients = `users on ${this.floorMapEntity?.name} with status of ${statuses.join(', ')} (total ${totalUsers})`;

    const options = {
      useApi: true,
      statuses,
      perimeterId,
      incidentId,
      recipients
    };

    this.openMultipleMessageModalWithOptions('Send Message', options);
  }
  private openMultipleMessageModalWithOptions(title: string, options: any, showCancel: boolean = false) {
    if (!options || !options.statuses || options.statuses.length === 0) {
      const modalRef = this.modalService.open(this.noUsersModal, {
        centered: true,
        backdrop: 'static'
      });
      modalRef.componentInstance.message = 'No users to notify';
      return;
    }

    const modalRef = this.modalService.open(MultipleMessageComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.options = options;
    modalRef.componentInstance.showCancel = showCancel;

    return modalRef.result;
  }
  toggleUserVisibility(user: User) {
    if (user.visible) {
      this.removeUser(user);
    } else {
      this.showUser(user);
    }
    user.visible = !user.visible;
    // Save visibility state to preserve across data refreshes
    if (user.deviceId) {
      this.userVisibilityMap.set(user.deviceId, user.visible);
    }
  }

  private restoreUserVisibility(): void {
    // Restore visibility state for all user lists
    const allLists = [this.data.userList1, this.data.userList2, this.data.userList3];
    allLists.forEach(list => {
      list.forEach(user => {
        if (user.deviceId && this.userVisibilityMap.has(user.deviceId)) {
          user.visible = this.userVisibilityMap.get(user.deviceId) ?? true;
        }
      });
    });
  }
  showUser(user: any) {
    const incident = this.liveData;
    if (!incident) return;
    const entity = this.dataContext.getDevicemapEntityByDeviceId(user.deviceId);
    if (!entity) {
      let time;
      // If playback, set time as needed
      if (this.enablePlayback) {
        // let timestamp = Math.floor(this.coreService.getCurrentTime() / 1000) * 1000;
        // time = moment(timestamp).toISOString();
      }
      this.createIncidentDeviceMapEntity(user, incident, time);
      return;
    }
  }
  getDeviceMapEntityById(deviceId: string): any {
    if (this.liveData?.devices?.[deviceId]) {
      return this.liveData?.devices?.[deviceId];
    } else if (this.liveData?.building?.incidents?.[0]?.devices?.[deviceId]) {
      return this.liveData.building.incidents[0].devices[deviceId];
    }
    return undefined;
  }
  removeUser(user: any) {
    const entity = this.dataContext.getDevicemapEntityByDeviceId(user.deviceId);
    if (entity) {
    }
  }
  createIncidentDeviceMapEntity(user: any, incident: any, time?: string): void {
    let api$: Observable<any>;
    if (this.enablePlayback) {
      // api$ = this.deviceService.getIncidentDeviceHistoryDataByDeviceId(user, incident, time);
      api$ = this.deviceService.getIncidentDeviceDataByDeviceId(user, incident);
    } else {
      api$ = this.deviceService.getIncidentDeviceDataByDeviceId(user, incident);
    }
    api$.subscribe({
      next: () => {
        // Device entity created/updated
      },
      error: (error) => {
        console.error('Error creating/updating device map entity:', error);
      }
    });
  }
  showUserInfo(user: any) {
    const entity = this.getDeviceMapEntityById(user.deviceId);
    if (entity) {
      this.showUsersInfoInWindow(entity);
    }
  }
  showUsersInfoInWindow(entity: any) {
    // Check if the details window for this entity is already open
    if (!this.windowService.isEntityDetailsWindowOpen(entity.getWindowId())) {
      // Open the details window (or handle click logic)
      this.globeService.handleClick(this, entity);
    }
  }
  addUpdate(): void {
    // fly to building if available (same behavior as legacy)
    if (this.mapentityid && typeof this.mapentityid.initViewPosLon === 'number' && typeof this.mapentityid.initViewPosLat === 'number') {
      const range = 300;
      const offset = new Cesium.HeadingPitchRange(
        this.mapentityid.initViewHeading,
        this.mapentityid.initViewPitch,
        range
      );
      const viewer = this.core.getViewer();
      try {
        viewer.camera.flyToBoundingSphere(
          new Cesium.BoundingSphere(
            Cesium.Cartesian3.fromDegrees(
              this.mapentityid.initViewPosLon,
              this.mapentityid.initViewPosLat,
              1
            )
          ),
          { duration: 0, offset }
        );
      } catch (e) {
      }
    }

    this.showGlobeClick = true;
    this.toggleUIVisibility(true);

    this.pluginContext.addGlobeClickListener(
      'incidentattributevisualizerOnClick',
      this.updateGlobeClick.bind(this)
    );
  }

  onGlobeClickClose(): void {
    try {
      this.pluginContext.removeGlobeClickListener('incidentattributevisualizerOnClick');
    } catch (e) { }
    this.showGlobeClick = false;
    this.toggleUIVisibility(false);
  }
  private toggleUIVisibility(hide: boolean) {
    const selectors = ['.details-window', '.right-panel', '.left-panel'];
    selectors.forEach((sel) => {
      Array.from(document.querySelectorAll(sel)).forEach((el: any) => {
        try {
          if (hide) {
            this.renderer.setStyle(el, 'display', 'none');
          } else {
            // details-window should be block; left/right panels should be flex
            if (sel === '.details-window') {
              this.renderer.setStyle(el, 'display', 'block');
            } else {
              this.renderer.setStyle(el, 'display', 'flex');
            }
          }
        } catch (e) {
          // ignore DOM errors
        }
      });
    });
  }
  updateGlobeClick(lat: number, lon: number): void {
    const data: any = {};
    const sessionOption = this.sessionOptionService.getSessionData();
    if (sessionOption) {
      data.eventId =
        sessionOption.eventId ??
        sessionOption.eventOption?.eventId ??
        sessionOption.eventOption?.incidentId ??
        sessionOption.eventOption?.id ??
        sessionOption.event?.id ?? sessionOption.event?.incidentId ??
        undefined;
    }

    // data.floor = this.floorMapEntity || null;
    data.lat = lat;
    data.lon = lon;

    try {
      this.pluginContext.removeGlobeClickListener('incidentattributevisualizerOnClick');
    } catch (e) { }
    this.showGlobeClick = false;
    this.toggleUIVisibility(true);

    if (this.mapentityid && typeof this.mapentityid.initViewPosLon === 'number' && typeof this.mapentityid.initViewPosLat === 'number') {
      const range = 300;
      const offset = new Cesium.HeadingPitchRange(
        this.mapentityid.initViewHeading,
        this.mapentityid.initViewPitch,
        range
      );
      const center = Cesium.Cartesian3.fromDegrees(
        this.mapentityid.initViewPosLon,
        this.mapentityid.initViewPosLat,
        10
      );
      try {
        const viewer = this.core.getViewer();
        viewer.camera.lookAt(center, offset);
      } catch (e) { }
    }

    this.openUpdateModal(data);
  }

  openUpdateModal(data: any) {
    const modalRef = this.modalService.open(CreateUpdateDialogComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.data = data;

    const restoreUi = () => {
      try {
        this.toggleUIVisibility(false);
        this.showGlobeClick = false;
      } catch (e) {
      }
    };

    if (modalRef.result && typeof modalRef.result.finally === 'function') {
      modalRef.result.finally(restoreUi);
    } else if (modalRef.result) {
      modalRef.result.then(restoreUi).catch(restoreUi);
    }

    return modalRef.result;
  }
  getNumberOfSafeUsersForPerimeter(perimeter: Perimeter): number {
    return perimeter.getNumberOfSafeUsers();
  }
  getNumberOfUnSafeUsersForPerimeter(perimeter: Perimeter): number {
    return perimeter.getNumberOfUnSafeUsers();
  }
  viewInfo(camera: any) {
    /* ... */
  }
  viewCamera(camera: any) {
    /* ... */
  }
  viewTaskStepInfo(step: any) {
    /* ... */
  }
  markTaskStepAsUncompleted(id: number) {
    /* ... */
  }
  markTaskStepAsCompleted(id: number) { }
  close() {
    this.floorService.updateIncidentVisualizer(false);
    this.closeRequested.emit(); // Emit close event
    if (this.enablePlayback) {
      this.arcAngelPlaybackMode.closeIncidentVisualizerFromOutside();
    } else if (this.enableInteraction) {
      this.arcAngelLiveMode.closeIncidentVisualizerFromOutside();
    }
  }
  numberOfBeaconsChange(event: number) {
    this.numberOfBeacons = event;
  }
  numberOfCamerasChange(event: number) {
    this.numberOfCameras = event;
  }
  changeBeacons(event: any) {
    if (event.target.checked) {
      this.beaconsVisible = true;
    } else {
      this.beaconsVisible = false;
    }
  }
  changeCameraClick(event: any) {
    if (event.target.checked) {
      this.cameraVisible = true;
    } else {
      this.cameraVisible = false;
    }
  }

  userOnFloorsChange(data: any) {
    // Handle the userOnFloorsChange event
    const newUsers = data.users || [];

    // Only update if we have new users to prevent clearing existing data
    if (!newUsers || newUsers.length === 0) {
      // If no users provided, just update counts if available, but don't clear lists
      if (data.totalUserCount !== undefined) {
        this.data.totalUserCount = data.totalUserCount;
        this.data.lastPage = Math.max(1, Math.ceil(this.data.totalUserCount / this.data.limit));
        // Ensure current page is valid
        if (this.data.page > this.data.lastPage) {
          this.data.page = this.data.lastPage;
        }
        if (this.data.page < 1) {
          this.data.page = 1;
        }
      }
      return;
    }

    const previousTotalCount = this.data.totalUserCount;

    this.data.totalUserCount = data.totalUserCount ?? newUsers.length;
    this.fullUsers = newUsers; // Store all users for pagination
    this.data.lastPage = Math.max(1, Math.ceil(this.data.totalUserCount / this.data.limit));

    // Only reset page if total count changed significantly (e.g., new data load)
    if (Math.abs(this.data.totalUserCount - previousTotalCount) > 10) {
      this.data.page = 1;
    }

    // Ensure current page is valid
    if (this.data.page > this.data.lastPage) {
      this.data.page = this.data.lastPage;
    }
    if (this.data.page < 1) {
      this.data.page = 1;
    }

    // Only update user lists if status filter is NOT active
    // This prevents overwriting the filtered results when checkboxes change
    if (!this.isStatusFilterActive) {
      this.updateUserListsForPage(); // Update lists based on current page

      // Restore visibility state from map
      this.restoreUserVisibility();

      // Save originals for restoring after search
      this.origUserList1 = [...this.data.userList1];
      this.origUserList2 = [...this.data.userList2];
      this.origUserList3 = [...this.data.userList3];

      const combined = [
        ...this.data.userList1,
        ...this.data.userList2,
        ...this.data.userList3,
      ];
      // keep both places populated to avoid regressions where code expects either
      // Combine all lists into one array
      this.data.allUsers = combined;
      this.allUsers = combined;
    } else {
      // If filter is active, reapply the status filter
      this.statusChanged();
      // Restore visibility state after status filter
      this.restoreUserVisibility();
    }

    // If a search is active, reapply the filter so UI stays consistent
    if (this.searchQuery && this.searchQuery.length > 0) {
      this.applyFilteredToColumns();
    }

    // Count users by status - using fullUsers to get counts from ALL pages, not just current page
    const statusCounts = (this.fullUsers || []).reduce((acc: Record<string, number>, user: any) => {
      const status = user?.status || 'UNKNOWN'; // handle missing status
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Assign counts to data object
    this.data.unknownCount = statusCounts['UNKNOWN'] || 0;
    this.data.safeCount = statusCounts['SAFE'] || 0;
    this.data.dangerCount = statusCounts['DANGER'] || 0;
    this.data.suspectCount = statusCounts['SUSPECT'] || 0;
  }

  jumpPage(page: number): void {
    this.data.page += page;
    // Ensure page stays within bounds
    if (this.data.page < 1) {
      this.data.page = 1;
    }
    if (this.data.page > this.data.lastPage) {
      this.data.page = this.data.lastPage;
    }

    this.updateUserListsForPage(); // Update lists based on new page

    // if (this.isPlaybackMode) {
    //   this.getUserForStreetPlayback();
    // } else {
    //   this.getUserForStreet();
    // }
  }

  private updateUserListsForPage(): void {
    // Ensure page is valid
    if (this.data.page > this.data.lastPage) {
      this.data.page = this.data.lastPage;
    }
    if (this.data.page < 1) {
      this.data.page = 1;
    }

    const startIndex = (this.data.page - 1) * this.data.limit;
    const endIndex = startIndex + this.data.limit;
    // Use filtered users if search is active, otherwise use all users
    const sourceUsers = this.searchQuery && this.searchQuery.length > 0 ? this.filteredUsers : this.fullUsers;
    const pageUsers = sourceUsers.slice(startIndex, endIndex);

    this.data.userList1 = pageUsers.slice(0, Math.ceil(pageUsers.length / 3));
    this.data.userList2 = pageUsers.slice(
      Math.ceil(pageUsers.length / 3),
      Math.ceil((pageUsers.length * 2) / 3)
    );
    this.data.userList3 = pageUsers.slice(
      Math.ceil((pageUsers.length * 2) / 3)
    );
  }

  cameraLayerChange(cameraList: any) {
    // Handle the userOnFloorsChange event
    this.cameraListForFloor = cameraList;
  }
  perimeterChange(perimeter: any) {
    if (perimeter) {
      let perimeterArray: any[] = [];
      _.forEach(perimeter, (perimeter) => {
        let perimeterObj = {
          name: perimeter.name,
          layerPath: perimeter.layerPath,
        };
        perimeterArray.push(perimeterObj);
      });
      this.perimeters = perimeterArray;
    }
  }
  updateChanges(updates: any) {
    if (!updates) {
      this.updates = [];
      this.cdr.detectChanges();
      return;
    }

    this.updates = Array.isArray(updates) ? updates : [updates];
    this.cdr.detectChanges();
  }

  // --- Search helpers ---
  toggleSearch(): void {
    this.searchVisible = !this.searchVisible;
    if (!this.searchVisible) {
      this.clearSearch();
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filteredUsers = [];
    // restore original columns if we have them
    if (this.origUserList1.length || this.origUserList2.length || this.origUserList3.length) {
      this.data.userList1 = [...this.origUserList1];
      this.data.userList2 = [...this.origUserList2];
      this.data.userList3 = [...this.origUserList3];
    }
  }

  onSearchChange(_: any): void {
    const q = (this.searchQuery || '').trim().toLowerCase();
    if (!q) {
      this.filteredUsers = [];
      if (this.origUserList1.length || this.origUserList2.length || this.origUserList3.length) {
        this.data.userList1 = [...this.origUserList1];
        this.data.userList2 = [...this.origUserList2];
        this.data.userList3 = [...this.origUserList3];
      }
      this.data.totalUserCount = this.fullUsers?.length || 0;
      return;
    }
    const src = this.fullUsers || [];
    this.filteredUsers = src.filter((u: any) => (u?.name || '').toLowerCase().includes(q));
    this.data.page = 1;
    this.data.totalUserCount = this.filteredUsers.length;
    this.data.lastPage = Math.max(1, Math.ceil(this.filteredUsers.length / this.data.limit));
    this.updateUserListsForPage();
  }

  private applyFilteredToColumns(): void {
    const users = this.filteredUsers || [];
    const len = users.length;
    const col1End = Math.ceil(len / 3);
    const col2End = Math.ceil((len * 2) / 3);
    this.data.userList1 = users.slice(0, col1End);
    this.data.userList2 = users.slice(col1End, col2End);
    this.data.userList3 = users.slice(col2End);
  }

  /// fetch user details from the server
  fetchProfilesFromOrganization(user: any): void {
    this.spinnerService.show();
    const title = 'User Details';
    this.organizationService
      .getProfilesFromOrganization(user.userId)
      .pipe(
        switchMap((userInfo: any) => {
          if (!userInfo) {
            return of({
              userInfo: null,
              pictureBlob: null,
              emergencyNumber: null,
              suspectDetails: null,
            });
          }
          const pictureId = userInfo?.UserPictures?.[0]?.id || null;
          const organizationId =
            userInfo?.UserOrganizations?.[0]?.Organization?.id || null;
          /// check if user is suspect
          if (user.status.toLowerCase() === 'suspect') {
            let userPhone = userInfo?.UserPhones?.[0]?.number || null;
            if (userPhone) {
              userPhone = userPhone.replace(/\D/g, '');
              if (userPhone.length > 10) {
                userPhone = userPhone.slice(-10);
              }
              return this.organizationService
                .getSuspectDetailByPhoneNumber(userPhone)
                .pipe(
                  switchMap((suspectDetails: any) => {
                    if (!pictureId) {
                      return of({
                        userInfo,
                        pictureBlob: null,
                        emergencyNumber: null,
                        suspectDetails,
                      });
                    }
                    return this.organizationService
                      .getUserPicture(pictureId)
                      .pipe(
                        catchError((error) => {
                          return of(null);
                        }),
                        map((pictureBlob) => ({
                          userInfo,
                          pictureBlob,
                          emergencyNumber: null,
                          suspectDetails,
                        }))
                      );
                  })
                );
            }
            return of({
              userInfo,
              pictureBlob: null,
              emergencyNumber: null,
              suspectDetails: null,
            });
          }

          return this.organizationService.getEmergencyNumber().pipe(
            switchMap((emergencyNumbers: any) => {
              const emergencyNumber = this.findEmergencyNumber(
                emergencyNumbers,
                organizationId
              );
              if (!pictureId) {
                return of({
                  userInfo,
                  pictureBlob: null,
                  emergencyNumber,
                  suspectDetails: null,
                });
              }
              return this.organizationService.getUserPicture(pictureId).pipe(
                catchError((error) => {
                  return of(null);
                }),
                map((pictureBlob) => ({
                  userInfo,
                  pictureBlob,
                  emergencyNumber,
                  suspectDetails: null,
                }))
              );
            })
          );
        })
      )
      .subscribe({
        next: ({ userInfo, pictureBlob, emergencyNumber, suspectDetails }) => {
          if (!userInfo) return;
          userInfo.picture = pictureBlob
            ? URL.createObjectURL(pictureBlob)
            : null;
          userInfo.emergencyNumber = emergencyNumber;
          let suspectDetail = suspectDetails || null;
          this.openDetailsModal(title, userInfo, suspectDetail, true);
          this.spinnerService.hide();
          //this.handleFlyToUserLocation(userInfo);
        },
        error: (err: any) => {
          console.error('Error fetching user data:', err);
          this.errorModal('Error', 'Failed to fetch user data.', true);
          this.spinnerService.hide();
        },
        complete: () => {
          this.spinnerService.hide();
        },
      });
  }

  // 🔹 find the emergency number from response
  private findEmergencyNumber(
    emergencyNumbers: any,
    organizationId: string | null
  ): string | null {
    if (
      emergencyNumbers &&
      Array.isArray(emergencyNumbers.rows) &&
      organizationId
    ) {
      const match = emergencyNumbers.rows.find(
        (item: any) => item.organizationId === organizationId
      );
      return match?.number || null;
    }
    return null;
  }
  private openDetailsModal(
    title: string,
    data: any,
    suspectDetails: any = null,
    shouldCallApi: boolean
  ) {
    const modalRef = this.modalService.open(DetailsModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
    });
    if (suspectDetails) {
      modalRef.componentInstance.title = 'POI Info';
      modalRef.componentInstance.suspectData = suspectDetails;
    } else {
      modalRef.componentInstance.title = title;
    }
    modalRef.componentInstance.userInfo = data;
    modalRef.componentInstance.showCancel = false;
    modalRef.componentInstance.shouldCallApi = shouldCallApi;


    return modalRef.result;
  }
  private errorModal(title: string, data: any, showCancel: boolean = false) {
    const modalRef = this.modalService.open(DetailsModalComponent, {
      size: 'md',
      centered: true,
      backdrop: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = data;
    modalRef.componentInstance.showCancel = showCancel;

    return modalRef.result;
  }

  // Method called by close button
  onCloseClick(): void {
    this.closeRequested.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Clean up any subscriptions here
    if (this.closeRequested && !this.closeRequested.closed) {
      this.closeRequested.complete();
    }
  }

  onMapCollapseChange(collapsed: boolean) {
    this.showTabs = collapsed;
  }
  getCurrentIncidentTime(): string {
    const incident = this.getCurrentIncident();
    if (!incident) {
      return '0:00:00'; // Return default value if no incident is available
    }
    const elapsedTime = this.getElapsedTime(incident.timestamp, this.core.getCurrentTimestamp() - 1000);
    return elapsedTime.startsWith('-') ? '0:00:00' : elapsedTime;
  }
  private getElapsedTime(dateStart: any, dateCurrent: any): string {
    const duration = moment.duration(moment(dateCurrent).diff(moment(dateStart)));
    return (
      Math.floor(duration.asHours()) +
      moment.utc(duration.asMilliseconds()).format(':mm:ss')
    );
  }
  activateEvent(): void {
    const incident = this.incident || this.incidents?.[0];
    if (incident && !incident.isActive()) {
      this.arcAngelService.activateIncident(incident.getEventId())
        .then(() => incident.setActive())
        .catch((error: any) => console.error('Error activating incident.', error));
    }
  }

  closeEvent(): void {
    const incident = this.incident || this.incidents?.[0];
    if (incident) {
      this.incidentCategoryService.setManagingOrgId(incident.orgid);
      if (!incident.isClosed()) {
        incident.setClosed();
        this.arcAngelService.updateStatusOfEvent(incident.getEventId(), incident.CLOSED_STATE);
      }
    }
  }

  resolveEvent() {
    const incident = this.incident || this.incidents?.[0];
    if (incident.obj.id) {
      const modalRef = this.modalService.open(ResponseModalComponent, {
        centered: true,
        backdrop: 'static',
        keyboard: false,
      });
      const component = modalRef.componentInstance as ResponseModalComponent;
      component.message = `Are you sure you want to resolve the event?`;
      component.showCancel = true;
      modalRef.result.then((confirmed: boolean) => {
        if (confirmed) {
          this.spinnerService.show();
          this.incidentCategoryService
            .updateStatusOfEvent(incident.obj.id, 'inactive')
            .subscribe(
              (result) => {
                if (result) {
                  this.sessionOptionService.setMode('Monitor Mode');
                  this.showReturningMonitorModePopup();
                }
                this.spinnerService.hide();
              },
              (error) => {
                this.spinnerService.hide();
                console.error('Error updating event status:', error);
              }
            );
        }
      });
    }
  }
  showReturningMonitorModePopup(): void {
    const modalRef = this.modalService.open(ResponseModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
    const component = modalRef.componentInstance as ResponseModalComponent;
    component.message = 'Event is resolved. Returning to Monitor Mode.';
    component.showCancel = false;
    modalRef.result.then(() => {
      this.sessionOptionService.goToMonitorMode();
      this.close();
      this.arcAngelLiveMode.resetCurrentLiveIncidentStatus();
    });
  }
  onCommandClick(index: number, mapEntity: any) {
    this.commands[index].execute(mapEntity);
  }
  /** Get safezones of first incident safely */

  get safezones(): SafeZone[] {
    // Prefer current incident; when mapentityid is undefined, fall back to liveData
    const incidentLike: any =
      this.getCurrentIncident() ??
      this.mapentityid?.incidents?.[0] ??
      this.liveData;
    const zones = incidentLike?.safezones ?? [];
    if (!Array.isArray(zones)) return [];
    return zones.filter((sz: any) => {
      const active =
        sz?.isActive ??
        sz?.safezone?.isActive ??
        sz?.active ??
        sz?.safezone?.active ??
        (typeof sz?.getIsActive === 'function' ? sz.getIsActive() : undefined);
      return active === true || active === 'true';
    });
  }

  /** Returns true if at least one safezone icon is visible on the globe */
  areSafezonesVisible(): boolean {
    const list = this.safezones;
    if (!Array.isArray(list)) return false;
    const viewer = this.core.getViewer();
    if (!viewer || !viewer.entities) return false;

    return list.some((sz: any) => {
      if (!sz) return false;

      // Collect possible entity IDs for this safezone
      const safezoneIds: string[] = [];

      if (sz.getId && typeof sz.getId === 'function') {
        safezoneIds.push(sz.getId());
      }

      if (sz.getArcAngelId && typeof sz.getArcAngelId === 'function') {
        const arcId = sz.getArcAngelId();
        safezoneIds.push(`arc-angel-${arcId}`);
        safezoneIds.push(`arc-angel-playback-${arcId}`);
      }

      // Check visibility by looking at entities in viewer
      for (const id of safezoneIds) {
        const entity = viewer.entities.getById(id);
        if (entity && entity.billboard) {
          const billboardShow = entity.billboard.show;
          // Handle Cesium ConstantProperty
          if (billboardShow && typeof billboardShow.getValue === 'function') {
            if (billboardShow.getValue() !== false) {
              return true;
            }
          } else if (billboardShow !== false) {
            return true;
          }
        }
      }

      return false;
    });
  }

  /** Toggle the safe zones list */
  toggleSafeZones(): void {
    this.showSafeZones = !this.showSafeZones;
  }

  /** Show/hide all safezone icons on the globe only */
  showHideAllSafezones(): void {
    const list = this.safezones;
    if (!Array.isArray(list) || list.length === 0) return;

    const viewer = this.core.getViewer();
    if (!viewer || !viewer.entities) return;

    // Collect all safezone entity IDs
    const safezoneIds: string[] = [];
    list.forEach((sz: any) => {
      if (!sz) return;
      // Try to get ID from SafeZoneMapEntity
      if (sz.getId && typeof sz.getId === 'function') {
        safezoneIds.push(sz.getId());
      }
      // Also try arcAngelId if available
      if (sz.getArcAngelId && typeof sz.getArcAngelId === 'function') {
        const arcId = sz.getArcAngelId();
        // SafeZone IDs are prefixed with 'arc-angel-' or 'arc-angel-playback-'
        safezoneIds.push(`arc-angel-${arcId}`);
        safezoneIds.push(`arc-angel-playback-${arcId}`);
      }
    });

    // Check current visibility by looking at entities in viewer
    let anyVisible = false;
    safezoneIds.forEach(id => {
      const entity = viewer.entities.getById(id);
      if (entity && entity.billboard) {
        const billboardShow = entity.billboard.show;
        // Handle Cesium ConstantProperty
        if (billboardShow && typeof billboardShow.getValue === 'function') {
          if (billboardShow.getValue() !== false) {
            anyVisible = true;
          }
        } else if (billboardShow !== false) {
          anyVisible = true;
        }
      }
    });

    const targetVisibility = !anyVisible;

    // Toggle each safezone icon on the globe
    safezoneIds.forEach(id => {
      const entity = viewer.entities.getById(id);
      if (entity && entity.billboard) {
        // Set billboard visibility directly
        if (entity.billboard.show && typeof entity.billboard.show.setValue === 'function') {
          // It's a Cesium Property
          entity.billboard.show.setValue(targetVisibility);
        } else {
          // Direct property assignment
          entity.billboard.show = targetVisibility;
        }
      }
    });

    // Also try using SafeZoneMapEntity methods as fallback
    list.forEach((sz: any) => {
      if (!sz) return;

      // Try to get entity from SafeZoneMapEntity and toggle
      if (typeof sz.getCesiumEntity === 'function') {
        const cesiumEntity = sz.getCesiumEntity();
        if (cesiumEntity && cesiumEntity.billboard) {
          if (cesiumEntity.billboard.show && typeof cesiumEntity.billboard.show.setValue === 'function') {
            cesiumEntity.billboard.show.setValue(targetVisibility);
          } else {
            cesiumEntity.billboard.show = targetVisibility;
          }
        }
      }
    });
  }

  /** Check if a specific safezone icon is visible on the globe */
  isSafezoneVisible(safezone: SafeZone): boolean {
    const sz = safezone as any;
    if (!sz) return false;

    const viewer = this.core.getViewer();
    if (!viewer || !viewer.entities) return false;

    // Collect possible entity IDs for this safezone
    const safezoneIds: string[] = [];

    if (sz.getId && typeof sz.getId === 'function') {
      safezoneIds.push(sz.getId());
    }

    if (sz.getArcAngelId && typeof sz.getArcAngelId === 'function') {
      const arcId = sz.getArcAngelId();
      safezoneIds.push(`arc-angel-${arcId}`);
      safezoneIds.push(`arc-angel-playback-${arcId}`);
    }

    // Check visibility by looking at entities in viewer
    for (const id of safezoneIds) {
      const entity = viewer.entities.getById(id);
      if (entity && entity.billboard) {
        const billboardShow = entity.billboard.show;
        // Handle Cesium ConstantProperty
        if (billboardShow && typeof billboardShow.getValue === 'function') {
          if (billboardShow.getValue() !== false) {
            return true;
          }
        } else if (billboardShow !== false) {
          return true;
        }
      }
    }

    return false;
  }

  /** Toggle visibility of individual safezone icon on the globe only */
  showHideEntity(safezone: SafeZone): void {
    const sz = safezone as any;
    if (!sz) return;

    const viewer = this.core.getViewer();
    if (!viewer || !viewer.entities) return;

    // Collect possible entity IDs for this safezone
    const safezoneIds: string[] = [];

    // Try to get ID from SafeZoneMapEntity
    if (sz.getId && typeof sz.getId === 'function') {
      safezoneIds.push(sz.getId());
    }

    // Also try arcAngelId if available
    if (sz.getArcAngelId && typeof sz.getArcAngelId === 'function') {
      const arcId = sz.getArcAngelId();
      // SafeZone IDs are prefixed with 'arc-angel-' or 'arc-angel-playback-'
      safezoneIds.push(`arc-angel-${arcId}`);
      safezoneIds.push(`arc-angel-playback-${arcId}`);
    }

    // Check current visibility by looking at entities in viewer
    let currentVisible = false;
    safezoneIds.forEach(id => {
      const entity = viewer.entities.getById(id);
      if (entity && entity.billboard) {
        const billboardShow = entity.billboard.show;
        // Handle Cesium ConstantProperty
        if (billboardShow && typeof billboardShow.getValue === 'function') {
          if (billboardShow.getValue() !== false) {
            currentVisible = true;
          }
        } else if (billboardShow !== false) {
          currentVisible = true;
        }
      }
    });

    const targetVisibility = !currentVisible;

    // Toggle this specific safezone icon on the globe
    safezoneIds.forEach(id => {
      const entity = viewer.entities.getById(id);
      if (entity && entity.billboard) {
        // Set billboard visibility directly
        if (entity.billboard.show && typeof entity.billboard.show.setValue === 'function') {
          // It's a Cesium Property
          entity.billboard.show.setValue(targetVisibility);
        } else {
          // Direct property assignment
          entity.billboard.show = targetVisibility;
        }
      }
    });

    // Also try using SafeZoneMapEntity method as fallback
    if (typeof sz.getCesiumEntity === 'function') {
      const cesiumEntity = sz.getCesiumEntity();
      if (cesiumEntity && cesiumEntity.billboard) {
        if (cesiumEntity.billboard.show && typeof cesiumEntity.billboard.show.setValue === 'function') {
          cesiumEntity.billboard.show.setValue(targetVisibility);
        } else {
          cesiumEntity.billboard.show = targetVisibility;
        }
      }
    }
  }

  /** Go to safezone (implement navigation logic) */
  goToEntity(safezone: SafeZone): void {
  }

  /** Add a new safezone */
  addSafeZone(): void {
    try {
      // Get the current live or map entity (like in ngOnInit)
      const arcLiveEntity = this.liveData || this.mapentityid;

      // If building incident → adjust Cesium camera view
      if (this.mapentityid && this.mapentityid.buildingMapEntity) {
        const building = this.mapentityid.buildingMapEntity;
        if (building && typeof building.initViewPosLon === 'number' && typeof building.initViewPosLat === 'number') {
          const range = 300;
          const offset = new Cesium.HeadingPitchRange(
            building.initViewHeading,
            building.initViewPitch,
            range
          );

          this.core.getViewer().camera.flyToBoundingSphere(
            new Cesium.BoundingSphere(
              Cesium.Cartesian3.fromDegrees(
                building.initViewPosLon,
                building.initViewPosLat,
                1
              )
            ),
            { duration: 0, offset }
          );
        }
      }

      // Open a modal to create a new Safe Zone (Angular way)
      const eventId = typeof arcLiveEntity.getEventId === 'function'
        ? arcLiveEntity.getEventId()
        : arcLiveEntity?.obj?.id || null;

      if (!eventId) {
        return;
      }
      this.addSafeZoneStatus.emit(true);
      this.floorService.setSafeZoneStatus(true, eventId);

    } catch (error) {
      console.error('Error in addSafeZone():', error);
    }
  }

  openEntity(update: any) {
  }

  toggleUpdates() {
    this.showUpdates = !this.showUpdates;
  }

  areUpdatesVisible(): boolean {
    return this.currUpdates?.some(u => u.cesiumEntity?.show);
  }

  showHideAllUpdates() {
    const shouldShow = !this.areUpdatesVisible();
    this.currUpdates?.forEach(u => u.cesiumEntity.show = shouldShow);
  }

  private showConfirm(message: string): Promise<boolean> {
    const modalRef = this.modalService.open(ResponseModalComponent, {
      centered: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top'
    });
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.showCancel = true;
    return modalRef.result.then(
      (res: any) => res === true,
      () => false
    );
  }

  private showAlert(message: string): Promise<any> {
    const modalRef = this.modalService.open(ResponseModalComponent, {
      centered: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top'
    });
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.showCancel = false;
    return modalRef.result.catch(() => {});
  }

  /** Check if a safezone is a perimeter safezone */
  isPerimeterSafezone(safezone: any): boolean {
    if (!safezone) return false;
    if (typeof safezone.isPerimeterSafezone === 'function') {
      return safezone.isPerimeterSafezone();
    }
    return !!(
      safezone.isPerimeterSafezone ||
      safezone.safezone?.isPerimeterSafezone ||
      safezone.isPerimeter ||
      safezone.safezone?.isPerimeter ||
      safezone.perimeterSafezoneId ||
      safezone.safezone?.perimeterSafezoneId ||
      safezone.perimterId ||
      safezone.safezone?.perimterId ||
      safezone.perimeterId ||
      safezone.safezone?.perimeterId ||
      safezone.perimeterSafezone ||
      safezone.safezone?.perimeterSafezone ||
      safezone.type === 'PERIMETER_SAFEZONE' ||
      safezone.safezone?.type === 'PERIMETER_SAFEZONE' ||
      safezone.type === 'PerimeterSafezone' ||
      safezone.safezone?.type === 'PerimeterSafezone' ||
      (typeof safezone.getPerimeterSafezoneId === 'function' && safezone.getPerimeterSafezoneId())
    );
  }

  /** Delete (deactivate) safezone */
  deleteSafezone(safezone: any, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (!safezone || this.isPerimeterSafezone(safezone)) return;

    const safezoneName = safezone.name || safezone.safezone?.label || 'this safe zone';
    this.showConfirm(`Are you sure you want to delete ${safezoneName}?`).then((confirmed: boolean) => {
      if (!confirmed) return;

      const cleanSafezoneId = String(
        safezone.arcAngelId ||
        safezone.safezone?.id ||
        (typeof safezone.getArcAngelId === 'function' ? safezone.getArcAngelId() : null) ||
        safezone.id ||
        (typeof safezone.getId === 'function' ? safezone.getId() : '')
      ).replace(/^arc-angel-(playback-)?/, '');

      const arcLiveEntity = this.liveData || this.mapentityid;
      const eventId = String(
        safezone.eventId ||
        safezone.safezone?.incidentId ||
        (typeof safezone.getEventId === 'function' ? safezone.getEventId() : null) ||
        (typeof arcLiveEntity?.getEventId === 'function' ? arcLiveEntity.getEventId() : null) ||
        arcLiveEntity?.obj?.id ||
        this.getCurrentIncident()?.id ||
        ''
      );

      const userOrgId = (typeof localStorage !== 'undefined' ? localStorage.getItem('PAA_org') : '') || '';
      const org =
        safezone.organizationId ||
        safezone.safezone?.organizationId ||
        safezone.safezone?.organization_id ||
        (this.arcAngelMonitorModeService ? this.arcAngelMonitorModeService.getOrgIdForLiveIncident(eventId) : null) ||
        this.getCurrentIncident()?.organizationId ||
        this.getCurrentIncident()?.obj?.organizationIds ||
        this.mapentityid?.organizationId ||
        this.liveData?.organizationId ||
        userOrgId ||
        '';
      const orgId = Array.isArray(org) ? String(org[0]) : String(org);

      this.spinnerService?.show?.();
      this.organizationService.deactivateSafezone(eventId, cleanSafezoneId, orgId).subscribe({
        next: () => {
          this.spinnerService?.hide?.();
          // Mark as inactive
          safezone.isActive = false;
          if (safezone.safezone) {
            safezone.safezone.isActive = false;
          }
          if (typeof safezone.setIsActive === 'function') {
            safezone.setIsActive(false);
          }

          // Hide and remove from Cesium viewer
          const viewer = this.core.getViewer();
          if (viewer && viewer.entities) {
            const possibleIds = [
              typeof safezone.getId === 'function' ? safezone.getId() : null,
              safezone.id,
              `arc-angel-${cleanSafezoneId}`,
              `arc-angel-playback-${cleanSafezoneId}`
            ].filter(Boolean);
            possibleIds.forEach((id: string) => {
              const entity = viewer.entities.getById(id);
              if (entity) {
                if (entity.billboard) {
                  entity.billboard.show = false;
                }
                viewer.entities.remove(entity);
              }
            });
            viewer.scene?.requestRender?.();
          }

          // Remove polygon if any
          if (typeof safezone.removePolygon === 'function') {
            safezone.removePolygon();
          }

          // Remove from DataContext
          if (this.dataContext) {
            try {
              this.dataContext.removeMapEntity(safezone, true);
            } catch (e) {}
          }

          // Remove from live or playback incident
          try {
            this.arcAngelLiveMode?.data?.liveIncident?.removeSafeZone?.(safezone);
            this.arcAngelPlaybackMode?.data?.playbackIncident?.removeSafeZone?.(safezone);
          } catch (e) {}

          // Remove from current incident safezones list if present
          const incidentLike: any =
            this.getCurrentIncident() ??
            this.mapentityid?.incidents?.[0] ??
            this.liveData;
          if (incidentLike && Array.isArray(incidentLike.safezones)) {
            const idx = incidentLike.safezones.findIndex(
              (sz: any) =>
                sz === safezone ||
                sz?.arcAngelId === cleanSafezoneId ||
                sz?.safezone?.id === cleanSafezoneId ||
                sz?.id === safezone?.id
            );
            if (idx > -1) {
              incidentLike.safezones.splice(idx, 1);
            }
          }

          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.spinnerService?.hide?.();
          console.error('Error deactivating safezone:', err);
          const errorMsg = 'Error deactivating safezone: ' + (err?.error?.message || err?.statusText || err?.message || 'Unknown error');
          this.showAlert(errorMsg);
        }
      });
    });
  }

}
